/**
 * WorkPublicationsTab — onglet "Publications" sur `<WorkDetailPage>`
 * (S6 PR4 + refactor 2026-05-10).
 *
 * Section unique pour TOUT ce qui touche aux publications cross-plateforme :
 *
 * 1. Cartes d'action par plateforme (`<PlatformPublishCard>`) — bouton
 *    "Publier", lifecycle inline (update/renew/end/delete). Une carte par
 *    plateforme `is_implemented=true` (S6 = Etsy seul ; S7+ ajouteront
 *    les autres).
 * 2. Journal cross-plateforme en lecture — état, mockups thumbnails,
 *    agrégats views/favorites/sales/revenu.
 *
 * Refactor 2026-05-10 : la `<PlatformPublishCard>` vivait avant dans
 * l'onglet Aperçu, ce qui fragmentait l'UX (publier ailleurs que là où
 * on voit l'historique). On la remonte ici pour que cet onglet soit
 * l'**unique** point d'entrée publications.
 */

import { Copy, ExternalLink } from 'lucide-react'
import { useMemo, useState } from 'react'

import PinterestDuplicatesModal from '@/components/work/PinterestDuplicatesModal'
import {
  countPinterestDuplicates,
  groupPinterestDuplicates,
} from '@/components/work/pinterest-duplicates'
import PinterestPublishCard from '@/components/work/PinterestPublishCard'
import PlatformPublishCard from '@/components/work/PlatformPublishCard'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import {
  useWorkPublicationsJournal,
  type PublicationJournalEntry,
} from '@/hooks/useWorkPublicationsJournal'
import { ETSY_LISTING_STATE_FR, PLATFORM_LABEL_FR } from '@/types/publications'
import type { WorkVariantWithCatalogResponse } from '@/types/works'

import './WorkPublicationsTab.css'

interface WorkPublicationsTabProps {
  workId: number
  workTitle: string
  variants: WorkVariantWithCatalogResponse[]
}

export default function WorkPublicationsTab({
  workId,
  workTitle,
  variants,
}: WorkPublicationsTabProps) {
  const journalQuery = useWorkPublicationsJournal({ workId })
  const entries = useMemo(() => journalQuery.data ?? [], [journalQuery.data])
  const [duplicatesOpen, setDuplicatesOpen] = useState(false)

  // Détection des doublons Pinterest pour activer / désactiver le bouton
  // (évite d'ouvrir le modal pour qu'il dise "rien à faire").
  const pinterestDuplicateCount = useMemo(
    () => countPinterestDuplicates(groupPinterestDuplicates(entries)),
    [entries],
  )

  return (
    <section className="work-publications-tab" aria-label="Publications">
      {/* ── Cartes d'action par plateforme ─────────────────────────────── */}
      <div className="work-publications-tab-actions">
        <PlatformPublishCard
          workId={workId}
          workTitle={workTitle}
          variants={variants}
          platform="etsy"
        />
        <PinterestPublishCard workId={workId} workTitle={workTitle} variants={variants} />
        {/* S8+ : Instagram / TikTok au même pattern dédié quand
            chaque plateforme aura sa carte spécifique */}
      </div>

      {/* ── Journal cross-plateforme ───────────────────────────────────── */}
      <div className="work-publications-tab-journal">
        <header className="work-publications-tab-journal-header">
          <h3 className="ui-h4 work-publications-tab-journal-title">Journal</h3>
          {pinterestDuplicateCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDuplicatesOpen(true)}
              title={`${pinterestDuplicateCount} Pin${pinterestDuplicateCount > 1 ? 's' : ''} Pinterest doublonné${pinterestDuplicateCount > 1 ? 's' : ''} détecté${pinterestDuplicateCount > 1 ? 's' : ''}`}
            >
              <Copy aria-hidden="true" size={14} />
              Détecter doublons Pinterest ({pinterestDuplicateCount})
            </Button>
          ) : null}
        </header>
        {journalQuery.isLoading ? (
          <p className="ui-body ui-text-muted" role="status">
            Chargement du journal…
          </p>
        ) : journalQuery.isError ? (
          <p className="ui-body ui-text-muted">
            Impossible de charger le journal des publications.
          </p>
        ) : entries.length === 0 ? (
          <p className="ui-body ui-text-muted">
            Aucune publication n'a encore été poussée sur une plateforme.
          </p>
        ) : (
          <ol className="work-publications-tab-list">
            {entries.map((entry) => (
              <PublicationRow key={entry.id} entry={entry} />
            ))}
          </ol>
        )}
      </div>

      {duplicatesOpen ? (
        <PinterestDuplicatesModal
          isOpen={duplicatesOpen}
          onClose={() => setDuplicatesOpen(false)}
          workId={workId}
          entries={entries}
        />
      ) : null}
    </section>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// PublicationRow
// ──────────────────────────────────────────────────────────────────────────────

interface PublicationRowProps {
  entry: PublicationJournalEntry
}

interface SkippedVariantMeta {
  sku: string
  size_label: string
  width_cm: number
  height_cm: number
  reason: string
}

function readSkippedVariants(state_metadata: unknown): SkippedVariantMeta[] {
  if (!state_metadata || typeof state_metadata !== 'object') return []
  const raw = (state_metadata as Record<string, unknown>)['skipped_variants']
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (v): v is SkippedVariantMeta =>
      v !== null &&
      typeof v === 'object' &&
      typeof (v as Record<string, unknown>).width_cm === 'number' &&
      typeof (v as Record<string, unknown>).height_cm === 'number',
  )
}

function PublicationRow({ entry }: PublicationRowProps) {
  const platformLabel = PLATFORM_LABEL_FR[entry.platform] ?? entry.platform
  const stateLabel = ETSY_LISTING_STATE_FR[entry.state] ?? entry.state
  const stateBadgeVariant: 'success' | 'warning' | 'default' =
    entry.state === 'active'
      ? 'success'
      : entry.state === 'ended' || entry.state === 'deleted'
        ? 'default'
        : 'warning'

  const publishedDate = entry.published_at
    ? new Date(entry.published_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—'

  const revenueEuro = (entry.revenue_cents / 100).toFixed(2)

  const skipped = readSkippedVariants(entry.state_metadata)
  const skippedRatios = skipped
    .map((v) => `${Math.round(v.width_cm)}×${Math.round(v.height_cm)}`)
    .filter((s, i, arr) => arr.indexOf(s) === i)
  const skippedRatiosPreview = skippedRatios.slice(0, 5).join(', ')
  const skippedRatiosMore = skippedRatios.length > 5 ? ` (+${skippedRatios.length - 5} autres)` : ''

  // Cap dur Etsy = 10 images par listing : surface les annexes shop
  // tronquées (dropped_carousel_count posé par l'adapter).
  const droppedCarouselRaw =
    entry.state_metadata && typeof entry.state_metadata === 'object'
      ? (entry.state_metadata as Record<string, unknown>)['dropped_carousel_count']
      : undefined
  const droppedCarousel =
    typeof droppedCarouselRaw === 'number' && droppedCarouselRaw > 0 ? droppedCarouselRaw : 0

  return (
    <li className="work-publications-tab-row">
      <header className="work-publications-tab-row-header">
        <div className="work-publications-tab-row-meta">
          <Badge variant="default" size="sm">
            {platformLabel}
          </Badge>
          <Badge variant={stateBadgeVariant} size="sm" dot={entry.state === 'active'}>
            {stateLabel}
          </Badge>
          {entry.draft_version_label ? (
            <span className="ui-body-sm ui-text-muted">{entry.draft_version_label}</span>
          ) : null}
        </div>
        <span className="ui-body-sm ui-text-muted">{publishedDate}</span>
      </header>

      {skipped.length > 0 ? (
        <div className="ui-alert ui-alert--warning" role="status">
          <strong>{skipped.length} taille(s) exclue(s) du listing</strong> — ratios non couverts par
          les masters de l'œuvre : {skippedRatiosPreview}
          {skippedRatiosMore}. Ajoute un master au bon ratio puis re-publie pour les réintégrer
          automatiquement.
        </div>
      ) : null}

      {droppedCarousel > 0 ? (
        <div className="ui-alert ui-alert--warning" role="status">
          <strong>{droppedCarousel} image(s) shop ignorée(s)</strong> — cap Etsy de 10 images par
          listing atteint. Réduis le nombre de mockups ou d'annexes shop avant le prochain publish
          pour les réintégrer.
        </div>
      ) : null}

      {entry.mockups.length > 0 ? (
        <div
          className="work-publications-tab-thumbs"
          aria-label={`${entry.mockups_total} mockup${entry.mockups_total > 1 ? 's' : ''} attaché${entry.mockups_total > 1 ? 's' : ''}`}
        >
          {entry.mockups.map((m) => (
            <Thumbnail
              key={m.mockup_id}
              src={m.thumbnail_url}
              alt={m.template_name}
              rank={m.rank}
            />
          ))}
          {entry.mockups_total > entry.mockups.length ? (
            <div
              className="work-publications-tab-thumb-more"
              aria-label={`${entry.mockups_total - entry.mockups.length} mockup(s) supplémentaire(s)`}
            >
              +{entry.mockups_total - entry.mockups.length}
            </div>
          ) : null}
        </div>
      ) : null}

      <dl className="work-publications-tab-stats">
        <div>
          <dt className="ui-body-sm ui-text-muted">Vues</dt>
          <dd className="ui-body">{entry.views_total.toLocaleString('fr-FR')}</dd>
        </div>
        <div>
          <dt className="ui-body-sm ui-text-muted">Favoris</dt>
          <dd className="ui-body">{entry.favorites_total.toLocaleString('fr-FR')}</dd>
        </div>
        <div>
          <dt className="ui-body-sm ui-text-muted">Ventes</dt>
          <dd className="ui-body">{entry.sales_count}</dd>
        </div>
        <div>
          <dt className="ui-body-sm ui-text-muted">Revenu</dt>
          <dd className="ui-body">{revenueEuro} €</dd>
        </div>
      </dl>

      {entry.url ? (
        <a
          href={entry.url}
          target="_blank"
          rel="noopener noreferrer"
          className="work-publications-tab-row-link ui-body-sm"
        >
          Voir sur {platformLabel} <ExternalLink aria-hidden="true" size={14} />
        </a>
      ) : (
        <span className="ui-body-sm ui-text-muted">#{entry.external_id}</span>
      )}
    </li>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Thumbnail (presigned MinIO TTL 5min)
// ──────────────────────────────────────────────────────────────────────────────

interface ThumbnailProps {
  src: string | null
  alt: string
  rank: number
}

function Thumbnail({ src, alt, rank }: ThumbnailProps) {
  if (src === null) {
    return (
      <div
        className="work-publications-tab-thumb work-publications-tab-thumb-empty"
        aria-label={`${alt} (rang ${rank}) — aucun composite généré`}
      >
        <span className="ui-text-muted">∅</span>
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      title={`${alt} (rang ${rank})`}
      className="work-publications-tab-thumb"
      loading="lazy"
    />
  )
}
